import { ElementType, Fragment } from 'react';
import { Box } from '@mui/material';
import { Trans } from 'react-i18next';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ScoreboardIcon from '@mui/icons-material/Scoreboard';
import { ROUTES } from '@routes/routes';
import { NotificationPresenter } from '@notifications/interfaces';
import { NotificationMessageBlock } from '@notifications/components/NotificationMessageBlock';
import { translate } from '@translations/i18n';
import { ScoreSubmissionDecisionNotificationData } from './interfaces';

const isScoreSubmissionDecisionPayload = (
  payload: unknown
): payload is ScoreSubmissionDecisionNotificationData => {
  if (typeof payload !== 'object' || payload === null) {
    return false;
  }

  return (
    'competitionName' in payload && typeof payload.competitionName === 'string'
  );
};

const createScoreSubmissionDecisionPresenter = (
  type: 'score-submission-approved' | 'score-submission-rejected',
  badgeIcon: ElementType
): NotificationPresenter<ScoreSubmissionDecisionNotificationData> => ({
  type,
  icon: ScoreboardIcon,
  badgeIcon,
  isPayload: isScoreSubmissionDecisionPayload,
  label: (payload, t) =>
    t(`notifications:types.${type}`, {
      competitionName: payload.competitionName,
    }),
  message: (payload) => (
    <Trans
      i18nKey={`notifications:menu.${type}`}
      values={{
        competitionName: payload.competitionName,
      }}
      components={{
        bold: <strong />,
      }}
    />
  ),
  body: (payload) => {
    if (type !== 'score-submission-rejected') {
      return null;
    }

    const reviewNote = payload.reviewNote?.trim();

    if (!reviewNote) {
      return null;
    }

    return (
      <NotificationMessageBlock>
        <Fragment>
          <Box component="span" sx={{ fontWeight: 'bold' }}>
            {translate('competitions:portal.yourScores.reviewNote')}
          </Box>
          {' '}
          {reviewNote}
        </Fragment>
      </NotificationMessageBlock>
    );
  },
  path: (payload) => {
    if (!payload.competitionSlug) {
      return null;
    }

    return ROUTES.portal.competitions.details(payload.competitionSlug);
  },
});

export const scoreSubmissionDecisionPresenters: NotificationPresenter<ScoreSubmissionDecisionNotificationData>[] =
  [
    createScoreSubmissionDecisionPresenter(
      'score-submission-approved',
      CheckCircleIcon
    ),
    createScoreSubmissionDecisionPresenter(
      'score-submission-rejected',
      CancelIcon
    ),
  ];
